import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {Flex, Text, Icon, Button, Box, Link} from '@spothero/ui';
import IconInfoCircle from '@spothero/icons/info-circle-filled';
import IconSemiCircleCheckOverflow from '@spothero/icons/checkmark-circle-filled';
import {compose} from 'redux';
import withConfig from '@/config/withConfig';
import {Modal, ModalContent, ModalFooter} from '@spothero/ui-backlog';

export const setParkingValues = (
    cancellationAllowed,
    isInstantAccess,
    siteUrl
) => {
    const cancellationValues = {
        id: 0,
        text: 'Free Cancellation',
        modalTitle: 'Free Cancellation Policy',
        modalContent: (
            <>
                <Text paddingBottom={4}>
                    At this facility, you have up until the minute your
                    reservation begins to cancel your parking for a full refund.
                    You can cancel your reservation on the SpotHero website or
                    app.
                </Text>
                <Text>
                    If you&apos;re having trouble with your reservation and
                    it&apos;s after the start time, please reach out to our
                    Customer Heroes who are more than happy to help rectify any
                    situation!
                </Text>
            </>
        ),
    };

    const guaranteedParkingValues = {
        id: 1,
        text: 'Guaranteed parking',
        modalTitle: 'Guaranteed Parking by SpotHero',
        modalContent: (
            <>
                <Text paddingBottom={4}>
                    When you park and pay with SpotHero, we guarantee you will
                    have a spot to park in at the price you paid or your money
                    back.
                </Text>
                <Text paddingBottom={4}>
                    If you need help with your reservation, please contact us,
                    and we&apos;ll do our best to make it right. Our world-class
                    Customer Hero team is available 7 days a week, 365 days a
                    year.
                </Text>
                <Text>
                    For specifics, please refer to the{' '}
                    <Link href={`${siteUrl}/about/parking-guarantee`}>
                        SpotHero Parking Guarantee
                    </Link>
                    .
                </Text>
            </>
        ),
    };

    const instantAccessValues = {
        id: 2,
        text: 'Instant Access',
        modalTitle: 'Instant Access',
        modalContent: (
            <Text paddingBottom={4}>
                Skip the wait—start parking on day 1. These spots offer instant
                access via a QR code, license plate readers, or a quick on-site
                sign-up when you arrive.
            </Text>
        ),
    };

    if (isInstantAccess) {
        const baseValues = cancellationAllowed
            ? cancellationValues
            : guaranteedParkingValues;

        return [{...baseValues}, {...instantAccessValues}];
    }

    return cancellationAllowed
        ? [{...cancellationValues}, {...guaranteedParkingValues}]
        : [{...guaranteedParkingValues}];
};

const CheckoutSection = ({
    cancellationAllowed,
    isInstantAccess,
    onBookSpotClick,
    siteUrl,
}) => {
    const parkingValues = setParkingValues(
        cancellationAllowed,
        isInstantAccess,
        siteUrl
    );
    const [openModal, setOpenModal] = useState(false);
    const [modalContent, setModalContent] = useState('');
    const [modalHeader, setModalHeader] = useState('');

    const handleOpenModalClick = id => {
        const value = parkingValues.find(val => val.id === id);

        setModalHeader(value.modalTitle);
        setModalContent(value.modalContent);
        setOpenModal(true);
    };

    const handleCloseModalClick = () => {
        setModalHeader('');
        setModalContent('');
        setOpenModal(false);
    };

    return (
        <Box marginTop="8" marginBottom="8">
            <Flex
                justifyContent="space-between"
                height="inherit"
                alignItems="center"
            >
                <Box>
                    {parkingValues.map(({text, id}) => (
                        <Flex key={id} alignItems="center">
                            <Icon
                                as={IconSemiCircleCheckOverflow}
                                alt="Semi Circle Check Icon"
                                fontSize="sm"
                                fill="green.700"
                            />
                            <Text
                                fontSize="sm"
                                fontWeight="normal"
                                paddingRight="1.5"
                                paddingLeft="1"
                                data-testid="CheckoutSection-parking-value"
                            >
                                {text}
                            </Text>
                            <Button
                                variant="tertiary"
                                onClick={() => handleOpenModalClick(id)}
                            >
                                <Icon
                                    alt="Info Circle Icon"
                                    as={IconInfoCircle}
                                    fontSize="xs"
                                />
                            </Button>
                        </Flex>
                    ))}
                    {openModal && (
                        <Modal onHidden={handleCloseModalClick}>
                            <div className="CheckoutSectionBareMin-Modal">
                                <ModalContent>
                                    <Text
                                        fontSize="lg"
                                        fontWeight="semibold"
                                        paddingBottom="3"
                                    >
                                        {modalHeader}
                                    </Text>
                                    {modalContent}
                                </ModalContent>
                                <ModalFooter>
                                    <Button
                                        onClick={handleCloseModalClick}
                                        variant="primary"
                                    >
                                        Okay
                                    </Button>
                                </ModalFooter>
                            </div>
                        </Modal>
                    )}
                </Box>
                <Button
                    onClick={onBookSpotClick}
                    textTransform="none"
                    marginLeft="6"
                >
                    Proceed to Checkout
                </Button>
            </Flex>
        </Box>
    );
};

CheckoutSection.propTypes = {
    cancellationAllowed: PropTypes.bool,
    isInstantAccess: PropTypes.bool,
    onBookSpotClick: PropTypes.func.isRequired,
    siteUrl: PropTypes.string.isRequired,
};

CheckoutSection.defaultProps = {
    cancellationAllowed: true,
    isInstantAccess: false,
};

const enhance = compose(withConfig(['siteUrl']));

export default enhance(CheckoutSection);
